import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiSend, FiMessageCircle, FiCalendar, FiUser, FiCheckCircle, FiX, FiSquare, FiShield } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    appointment: false
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuccessMessage(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      message: '',
      appointment: false
    });
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Telefon',
      details: ['+41 76 625 07 64', 'Mo-Fr: 07:00-18:00 Uhr'],
      action: 'tel:+41766250764'
    },
    {
      icon: FiMail,
      title: 'E-Mail',
      details: ['info@hublertex.ch', 'Antwort innerhalb 24h'],
      action: 'mailto:info@hublertex.ch'
    },
    {
      icon: FiMapPin,
      title: 'Adresse',
      details: ['Weissensteinstrasse 49', '4923 Wynau, Schweiz'],
      action: 'https://maps.google.com'
    },
    {
      icon: FiClock,
      title: 'Notfalldienst',
      details: ['24/7 verfügbar', '+41 76 625 07 64'],
      action: 'tel:+41766250764'
    }
  ];

  return (
    <div>
      {/* Success Message */}
      {showSuccessMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 max-w-md mx-auto px-4"
        >
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <SafeIcon icon={FiCheckCircle} className="w-6 h-6 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold">Vielen Dank!</p>
              <p className="text-sm">Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns schnellstmöglich bei Ihnen melden.</p>
            </div>
            <button onClick={() => setShowSuccessMessage(false)} className="text-white hover:text-green-100 transition-colors">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Hero Section */}
      <section className="relative py-16 sm:py-20 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Kontakt</h1>
            <p className="text-lg sm:text-xl max-w-3xl mx-auto">
              Haben Sie Fragen oder benötigen Sie ein Angebot? Wir freuen uns auf Ihre Nachricht und beraten Sie gerne.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300"
              >
                <SafeIcon icon={info.icon} className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600">{detail}</p>
                  ))}
                </div>
                <a
                  href={info.action}
                  className="inline-block mt-4 text-orange-500 hover:text-orange-600 font-semibold transition-colors"
                >
                  Kontaktieren
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Senden Sie uns eine Nachricht</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Ihr Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="ihre.email@beispiel.ch"
                    />
                  </div>
                </div>
                <textarea
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Ihr Anliegen..."
                />
                
                <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
                  <button
                    type="submit"
                    className="w-full sm:w-auto flex-1 bg-orange-500 hover:bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                    <span>Nachricht senden</span>
                  </button>
                  <div className="w-full sm:w-auto text-center">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 shadow-sm">
                      🚧 Datenbank-Anbindung im Aufbau
                    </span>
                  </div>
                </div>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Direkter Kontakt</h3>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white">
                    <SafeIcon icon={FiUser} className="w-8 h-8" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg">Jasmin Hubler</div>
                    <div className="text-gray-600">Geschäftsführung</div>
                    <div className="text-orange-500 font-medium">info@hublertex.ch</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Standort Wynau</h3>
                <p className="text-gray-600 mb-4">
                  Hubler & Tex Facility Services<br />
                  Weissensteinstrasse 49<br />
                  4923 Wynau
                </p>
                <div className="bg-gray-200 h-48 rounded flex items-center justify-center text-gray-500">
                  <SafeIcon icon={FiMapPin} className="w-10 h-10" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;